import { IUnit } from 'types/fields.interface'

export const FoodKitchenOperatorType = {
  Internal: 1,
  Contractor: 2,
}

export const FoodKitchenSupplyMode = {
  ClientSupplied: 1,
  ContractorSupplied: 2,
}

export const FoodDishSourceType = {
  Prepared: 1,
  Purchased: 2,
}

export const FoodMealWindowRunStatus = {
  Open: 1,
  Ordered: 2,
  Served: 3,
  Finalized: 4,
}

export const FoodPlanGenerationStatus = {
  Generated: 1,
  SkippedNoConfigVersion: 2,
  SkippedNoMealWindows: 3,
  Failed: 4,
}

export const FoodExceptionType = {
  DietaryMismatch: 1,
  MissingKitchenConfirmation: 2,
  ProfileConflict: 3,
  UnresolvedOverrideNote: 4,
  StandbyInsufficient: 5,
  ConsumptionPostingFailed: 6,
  MealWindowWithoutPlan: 7,
}

export const FoodExceptionSeverity = {
  Low: 1,
  Medium: 2,
  High: 3,
  Critical: 4,
}

export const FoodExceptionStatus = {
  Open: 1,
  Resolved: 2,
  AutoClosed: 3,
}

export const FoodExceptionResolutionActionType = {
  ReassignKitchen: 1,
  AssignDietSafeStandby: 2,
  CancelMealIntent: 3,
  ConfirmOnBehalf: 4,
  CorrectProfile: 5,
  Comment: 6,
  RetryConsumptionPosting: 7,
  AdjustPlan: 8,
}

export const FoodMealRequestType = {
  Guest: 1,
  Emergency: 2,
}

export const FoodMealRequestStatus = {
  Pending: 1,
  Approved: 2,
  Rejected: 3,
  Cancelled: 4,
}

export const FoodMealRequestRouting = {
  Standard: 1,
  Standby: 2,
}

export const FoodProvisioningMode = {
  MessOrHall: 1,
  Packout: 2,
  OptOut: 3,
}

export const FoodProvisioningRecordStatus = {
  Planned: 1,
  Ordered: 2,
  Served: 3,
  Finalized: 4,
  Cancelled: 5,
}

export const FoodProvisioningRecordSourceType = {
  Entitled: 1,
  AdHoc: 2,
  StandBy: 3,
  ManualAdjustment: 4,
}

export const FoodConfigUnknownPresencePolicy = {
  NoMeal: 1,
  PlanMeal: 2,
  StandbyReserve: 3,
}

export const FoodMenuPlanStatus = {
  Draft: 1,
  Published: 2,
}

export const FoodConsumptionRunStatus = {
  Pending: 1,
  Posted: 2,
  SkippedContractor: 3,
  Failed: 4,
}

export const FoodBulkServiceMode = {
  Fixed: 1,
  PerServed: 2,
}

export const FoodInternalProductionOrderStatus = {
  Draft: 1,
  Confirmed: 2,
  Posted: 3,
}

export interface ILocationNodeSimpleResponse {
  id: number
  name: string
  type: number
}

export interface IInventoryItemForFoodResponse {
  id: number
  name: string
  sku: string
}

export interface IFoodDietaryTagResponse {
  id: number
  code: string
  name: string
  active: boolean
  createdAt: string
}

export interface ICreateFoodDietaryTagRequest {
  code: string
  name: string
}

export interface IUpdateFoodDietaryTagRequest {
  name: string
}

export interface IFoodKitchenResponse {
  id: number
  name: string
  operatorType: number
  supplyMode: number
  storeLocation: ILocationNodeSimpleResponse | null
  packoutSupport: boolean
  softCapacity: number
  standbyEnabled: boolean
  dietaryTagCapabilities: IFoodDietaryTagResponse[]
  active: boolean
  createdAt: string
}

export interface ICreateFoodKitchenRequest {
  name: string
  operatorType: number
  supplyMode: number
  storeLocationId?: number | null
  packoutSupport: boolean
  softCapacity: number
  standbyEnabled: boolean
  dietaryTagCodes: string[]
}

export interface IUpdateFoodKitchenRequest {
  name?: string
  operatorType?: number
  supplyMode?: number
  storeLocationId?: number | null
  packoutSupport?: boolean
  softCapacity?: number
  standbyEnabled?: boolean
  dietaryTagCodes?: string[]
}

export interface IFoodMealWindowDowVariantResponse {
  id: number
  daysInWeek: number[]
  serviceStart: string | null
  serviceEnd: string | null
  cutoffTime: string | null
  cutoffDayOffset: number | null
  defaultKitchen: IFoodKitchenResponse | null
}

export interface IFoodMealWindowResponse {
  id: number
  name: string
  serviceStart: string
  serviceEnd: string
  cutoffTime: string
  cutoffDayOffset: number
  defaultKitchen: IFoodKitchenResponse | null
  createdAt: string
  dowVariants: IFoodMealWindowDowVariantResponse[]
}

export interface ICreateFoodMealWindowDowVariantRequest {
  daysInWeek: number[]
  serviceStart?: string | null
  serviceEnd?: string | null
  cutoffTime?: string | null
  cutoffDayOffset?: number | null
  defaultKitchenId?: number | null
}

export interface ICreateFoodMealWindowRequest {
  name: string
  serviceStart: string
  serviceEnd: string
  cutoffTime: string
  cutoffDayOffset: number
  defaultKitchenId?: number | null
  dowVariants?: ICreateFoodMealWindowDowVariantRequest[]
}

export interface IUpdateFoodMealWindowRequest {
  name?: string
  serviceStart?: string
  serviceEnd?: string
  cutoffTime?: string
  cutoffDayOffset?: number
  defaultKitchenId?: number | null
  dowVariants?: ICreateFoodMealWindowDowVariantRequest[]
}

export interface IFoodCatchmentRuleResponse {
  id: number
  kitchen: IFoodKitchenResponse | null
  locationNode: ILocationNodeSimpleResponse | null
  department: {
    id: number
    name: string
  } | null
  isFallback: boolean
  createdAt: string
}

export interface ICreateFoodCatchmentRuleRequest {
  kitchenId: number
  locationNodeId?: number | null
  departmentId?: number | null
  isFallback?: boolean
}

export interface IUpdateFoodCatchmentRuleRequest {
  kitchenId?: number
  locationNodeId?: number | null
  departmentId?: number | null
  isFallback?: boolean
}

export interface IFoodWindowModeDefault {
  id: number
  windowDefId: number
  windowDefName: string
  mode: number
}

export interface IEmployeeFoodProfile {
  id: number
  user: IUserForFoodManagementResponse | null
  stickyKitchen: IFoodKitchenResponse | null
  windowModeDefaults: IFoodWindowModeDefault[]
  createdAt: string
  updatedAt: string
}

export interface IUpsertEmployeeFoodProfileRequest {
  stickyKitchenId?: number | null
  windowModeDefaults: {
    windowDefId: number
    mode: number
  }[]
}

export interface IFoodAttendanceReconciliation {
  noShowGraceMinutes: number
  cancelIntentOnNoShow: boolean
  applyShowRateCoefficient: boolean
}

export interface IFoodSitePolicy {
  maxMealsPerDay: number
  mealSelectionPriorityWindowIds: number[]
  unknownPresencePolicy: number
  defaultMode: number
  attendanceReconciliation: IFoodAttendanceReconciliation
}

export interface IUpsertFoodSitePolicyRequest {
  maxMealsPerDay: number
  mealSelectionPriorityWindowIds: number[]
  unknownPresencePolicy: number
  defaultMode: number
  attendanceReconciliation: IFoodAttendanceReconciliation
}

export interface IFoodConfigVersionPayload {
  policy: IFoodSitePolicy
  mealWindows: any[]
  kitchens: any[]
  catchmentRules: any[]
  standbyConfigs: any[]
}

export interface IFoodConfigVersion {
  id: number
  effectiveFromOperationalDay: string
  payload: IFoodConfigVersionPayload
  createdBy: IUserForFoodManagementResponse | null
  createdAt: string
}

export interface IUpsertFoodStandbyConfigRequest {
  standbyDishSkuId: number
  dietaryTagCodes: string[]
  minThreshold: number
  enabled: boolean
}

export interface IFoodStandbyConfigResponse {
  id: number
  standbyDishSku: IInventoryItemForFoodResponse
  dietaryTags: IFoodDietaryTagResponse[]
  minThreshold: number
  enabled: boolean
  quantityOnHand: number
  belowThreshold: boolean
}

export interface IKitchenDishSupplierResponse {
  id: number
  name: string
}

export interface IKitchenNormativeLineResponse {
  ingredient: IKitchenIngredientResponse | null
  amount: number
}

export interface IKitchenNormativeVersionResponse {
  id: number
  effectiveFrom: string
  lines: IKitchenNormativeLineResponse[]
  createdAt: string
}

export interface ICreateKitchenNormativeVersionRequest {
  effectiveFrom?: string | null
  lines: {
    ingredientId: number
    amount: number
  }[]
}

export interface IKitchenDishResponse {
  id: number
  name: string
  sourceType: number
  dietaryTags: IFoodDietaryTagResponse[]
  supplier: IKitchenDishSupplierResponse | null
  dishSku: IInventoryItemForFoodResponse | null
  latestNormativeVersion: IKitchenNormativeVersionResponse | null
  active: boolean
  createdAt: string
}

export interface ICreateKitchenDishRequest {
  name: string
  sourceType: number
  dietaryTagCodes: string[]
  supplierId?: number | null
  dishSkuId?: number | null
  lines?: {
    ingredientId: number
    amount: number
  }[]
  activate?: boolean | null
}

export interface IUpdateKitchenDishRequest {
  name?: string
  sourceType?: number
  dietaryTagCodes?: string[]
  supplierId?: number | null
  dishSkuId?: number | null
  lines?: {
    ingredientId: number
    amount: number
  }[]
  active?: boolean | null
}

export interface IKitchenIngredientResponse {
  id: number
  name: string
  sku: IInventoryItemForFoodResponse | null
  recipeUnit: IUnit | null
  stockToRecipeFactor: number
  skuLinkHealthy: boolean
  active: boolean
}

export interface ICreateKitchenIngredientRequest {
  name: string
  skuId: number
  unitId: number
  stockToRecipeFactor: number
}

export interface IUpdateKitchenIngredientRequest {
  name?: string
  skuId?: number | null
  unitId?: number | null
  stockToRecipeFactor?: number | null
  active?: boolean | null
}

export interface IFoodPreviewDietaryTagSetCount {
  dietaryTags: IFoodDietaryTagResponse[]
  count: number
}

export interface INextDayPreviewRow {
  kitchen: IFoodKitchenResponse
  windowDef: IFoodMealWindowResponse
  mode: number
  totalCount: number
  dietaryTagSets: IFoodPreviewDietaryTagSetCount[]
}

export interface IFoodPlanGenerationResponse {
  status: number
  lastRunAt: string | null
  recordsCreated: number
  recordsCancelled: number
  message: string | null
}

export interface IFoodDayCloseGateResponse {
  canFinalize: boolean
  blockingReasons: string[]
}

export interface IFoodExceptionSeveritySummaryResponse {
  critical: number
  high: number
  medium: number
  low: number
}

export interface IFoodMealWindowRunResponse {
  id: number
  operationalDay: string
  windowDef: IFoodMealWindowResponse | null
  status: number
  cutoffFiredAt: string | null
  lateFire: boolean
}

export interface IKitchenMenuPlanEntryResponse {
  dietaryTags: IFoodDietaryTagResponse[]
  dish: IKitchenDishResponse | null
}

export interface IFoodKitchenWindowSummaryResponse {
  kitchen: IFoodKitchenResponse
  ordered: number
  served: number
  standbyUsed: number
  expectedWaste: number
  confirmed: boolean
  standby: IFoodStandbyConfigResponse[]
  menuEntries: IKitchenMenuPlanEntryResponse[]
}

export interface IFoodWindowSummaryResponse {
  mealWindowRun: IFoodMealWindowRunResponse | null
  windowDef: IFoodMealWindowResponse
  status: number | null
  planned: number
  ordered: number
  served: number
  finalized: number
  cancelled: number
  expectedWaste: number
  manualAdjustmentQty: number
  manualAdjustmentReasons: string[]
  kitchens: IFoodKitchenWindowSummaryResponse[]
}

export interface IFoodDashboardResponse {
  operationalDay: string
  windows: IFoodWindowSummaryResponse[]
  openExceptions: IFoodExceptionSeveritySummaryResponse
  dayClose: IFoodDayCloseGateResponse
  planGeneration: IFoodPlanGenerationResponse | null
}

export interface IUserForFoodManagementResponse {
  id: number
  firstName: string
  lastName: string
}

export interface IFoodExceptionRecordResponse {
  id: number
  type: number
  severity: number
  operationalDay: string
  windowDef: IFoodMealWindowResponse | null
  kitchen: IFoodKitchenResponse | null
  person: IUserForFoodManagementResponse | null
  rootCause: string
  cause: number
  status: number
  provenance: number
  resolutionActionType: number | null
  resolutionComment: string | null
  resolvedBy: IUserForFoodManagementResponse | null
  legalActions: number[]
  createdAt: string
}

export interface IFoodHistoryDayResponse {
  operationalDay: string
  windowsFinalized: number
  mealsFinalized: number
  expectedWaste: number
  plateWaste: number
  openExceptions: number
}

export interface IFoodEmployeeWindowRecordChainStepResponse {
  status: number
  at: string | null
  reason: string | null
  source: number | null
}

export interface IFoodDepartmentForFoodResponse {
  id: number
  name: string
  status?: number
}

export interface IFoodEmployeeForWindowResponse {
  id: number
  recordId: number
  person: IUserForFoodManagementResponse | null
  department: IFoodDepartmentForFoodResponse | null
  mode: number | null
  source: number
  status: number
  kitchen: IFoodKitchenResponse | null
  dietaryTags: IFoodDietaryTagResponse[]
  recordChain: IFoodEmployeeWindowRecordChainStepResponse[]
}

export interface IConfirmFoodKitchenRequest {
  servedCount: number
  standbyUsed: {
    dietaryTagCodes: string[]
    qty: number
  }[]
  enteredOnBehalf?: boolean
  onBehalfReason?: string | null
  queuedAt?: string | null
}

export interface IFoodKitchenConfirmationResponse {
  id: number
  mealWindowRunId: number
  kitchen: IFoodKitchenResponse | null
  servedCount: number
  standbyUsed: {
    dietaryTags: IFoodDietaryTagResponse[]
    qty: number
  }[]
  enteredOnBehalf: boolean
  onBehalfReason: string | null
  confirmedAt: string
}

export interface ICreateFoodManualAdjustmentRequest {
  lines: {
    kitchenId: number
    dietaryTagCodes: string[]
    qty: number
  }[]
  reason: string
}

export interface IFoodManualAdjustmentResponse {
  id: number
  mealWindowRunId: number
  reason: string
  lines: {
    kitchen: IFoodKitchenResponse | null
    dietaryTags: IFoodDietaryTagResponse[]
    qty: number
  }[]
  createdBy: IUserForFoodManagementResponse | null
  createdAt: string
}

export interface IBulkCancelFoodRecordsRequest {
  recordIds: number[]
  reason: string
}

export interface IReverseFoodRecordRequest {
  correction: string
  qty: number
  reason: string
}

export interface IFoodReversalRecordResponse {
  id: number
  recordId: number
  correction: string
  qty: number
  reason: string
  createdAt: string
}

export interface IResolveFoodExceptionRequest {
  actionType: number
  comment?: string | null
}

export interface IFoodMealRequestLineResponse {
  dietaryTags: IFoodDietaryTagResponse[]
  qty: number
}

export interface IFoodMealRequestDayRoutingResponse {
  operationalDay: string
  kitchen: IFoodKitchenResponse | null
  routing: number
}

export interface IFoodMealRequestResponse {
  id: number
  type: number
  dateFrom: string
  dateTo: string
  windowDef: IFoodMealWindowResponse | null
  kitchenPreference: IFoodKitchenResponse | null
  lines: IFoodMealRequestLineResponse[]
  routingResultsPerDay: IFoodMealRequestDayRoutingResponse[]
  reason: string
  status: number
  createdBy: IUserForFoodManagementResponse | null
  decidedBy: IUserForFoodManagementResponse | null
  decidedAt: string | null
  decisionNote: string | null
  createdAt: string
}

export interface ICreateFoodMealRequestRequest {
  type: number
  dateFrom: string
  dateTo: string
  windowDefId: number
  lines: {
    dietaryTagCodes: string[]
    qty: number
  }[]
  kitchenPreferenceId?: number | null
  reason: string
}

export interface IDecideFoodMealRequestRequest {
  approve: boolean
  decisionNote?: string | null
}

export interface IUpsertKitchenMenuPlanRequest {
  operationalDay: string
  windowDefId: number
  kitchenId: number
  entries: {
    dietaryTagCodes: string[]
    dishId: number
  }[]
}

export interface IKitchenMenuPlanResponse {
  id: number
  operationalDay: string
  windowDef: IFoodMealWindowResponse | null
  kitchen: IFoodKitchenResponse | null
  status: number
  entries: IKitchenMenuPlanEntryResponse[]
  createdAt: string
}

export interface IKitchenFeasibilityLineResponse {
  ingredient: IKitchenIngredientResponse | null
  required: number
  available: number
  shortfall: number
}

export interface IKitchenFeasibilityResponse {
  menuPlan: IKitchenMenuPlanResponse
  plannedMeals: number
  demandIsFirm: boolean
  lines: IKitchenFeasibilityLineResponse[]
  hasShortfall: boolean
  estimatedCost: number
}

export interface IKitchenMenuCycleTemplateSlotRequest {
  dayIndex: number
  windowDefId: number
  dishId: number
  dietaryTagCodes: string[]
}

export interface IUpsertKitchenMenuCycleTemplateRequest {
  name: string
  lengthDays: number
  slots: IKitchenMenuCycleTemplateSlotRequest[]
}

export interface IKitchenMenuCycleTemplateSlotResponse {
  dayIndex: number
  windowDef: IFoodMealWindowResponse | null
  dish: IKitchenDishResponse | null
  dietaryTags: IFoodDietaryTagResponse[]
}

export interface IKitchenMenuCycleTemplateResponse {
  id: number
  name: string
  lengthDays: number
  slots: IKitchenMenuCycleTemplateSlotResponse[]
  createdAt: string
}

export interface IInstantiateKitchenMenuTemplateRequest {
  kitchenId: number
  startDate: string
  dryRun?: boolean
}

export interface IKitchenTemplateInstantiationResponse {
  filled: number
  skipped: number
  warnings: string[]
  dryRun: boolean
}

export interface IKitchenProductionPullBatchResponse {
  batchId: number
  expiryDate: string | null
  quantity: number
}

export interface IKitchenProductionPullLineResponse {
  ingredient: IKitchenIngredientResponse | null
  required: number
  available: number
  shortfall: number
  pullFrom: IKitchenProductionPullBatchResponse[]
}

export interface IKitchenProductionDishResponse {
  dish: IKitchenDishResponse | null
  dietaryTags: IFoodDietaryTagResponse[]
  portions: number
  packoutPortions: number
  normativeVersion: IKitchenNormativeVersionResponse | null
}

export interface IKitchenProductionReconciliationResponse {
  status: number
  postedAt: string | null
  failureReason: string | null
  ordered: number
  served: number
  plateWaste: number
  standbyUsed: number
  windowCost: number
  costPerMeal: number
}

export interface IKitchenContractorOrderLineResponse {
  dietaryTags: IFoodDietaryTagResponse[]
  dish: IKitchenDishResponse | null
  qty: number
}

export interface IKitchenContractorOrderResponse {
  id: number
  kitchen: IFoodKitchenResponse | null
  mealWindowRun: IFoodMealWindowRunResponse | null
  lines: IKitchenContractorOrderLineResponse[]
  createdAt: string
}

export interface IKitchenProductionWindowResponse {
  mealWindowRun: IFoodMealWindowRunResponse | null
  kitchen: IFoodKitchenResponse | null
  supplyMode: number
  menuPublished: boolean
  menuMissingWarning: boolean
  dishes: IKitchenProductionDishResponse[]
  pullList: IKitchenProductionPullLineResponse[]
  reconciliation: IKitchenProductionReconciliationResponse | null
  contractorOrder: IKitchenContractorOrderResponse | null
}

export interface IKitchenSuggestionResponse {
  dish: IKitchenDishResponse | null
  cookablePortions: number
  expiringIngredients: string[]
  daysToSoonestExpiry: number | null
  limitingIngredient: string | null
}

export interface IKitchenVarianceLineResponse {
  ingredient: IKitchenIngredientResponse | null
  theoretical: number
  countedAdjustment: number
  variancePercent: number
}

export interface IKitchenVarianceResponse {
  kitchen: IFoodKitchenResponse | null
  lines: IKitchenVarianceLineResponse[]
}

export interface IKitchenWasteResponse {
  kitchen: IFoodKitchenResponse | null
  expectedWaste: number
  plateWaste: number
  cooked: number
  served: number
  countAdjustment: number
}
